import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

/** Utils / Consts */
import routes from 'constants/routes';
import { logger } from 'services/api/logger';
import { getSSRInitialData, getSSRListsEndpoint } from 'utils/SSR';
import { getUserFromCookie } from 'utils/cookies';
import { generateAlgoliaAnalyticsTags } from 'utils/functions';
import { DEFAULT_TAB_LISTING_PAGES } from 'constants/listing-defaults';

/* Layout */
import FullWidthLayout from 'layouts/FullWidthLayout';
import LegacyCategoryLayout from 'components/Category/LegacyCategoryLayout';

import CategoryLayout from 'components/Category/CategoryLayout';
import FEATURE_FLAGS from 'constants/feature-flags';

const defaultProps = {
  categoryData: {
    slug: '',
    categoryData: {
      name: '',
      slug: '',
      description: '',
      shortDescription: '',
      seoTitle: '',
      seoDescription: '',
      parent: {},
      children: [],
    },
  },
  initialProductsData: {
    totalProducts: 0,
    products: [],
  },
  initialBrandsData: {
    totalBrands: 0,
    brands: [],
  },
  initialAlgoliaData: {
    filters: [],
    banners: [],
    products: [],
    totalProducts: 0,
    brands: [],
    totalBrands: 0,
    queryId: '',
  },
  initialQueryParams: {},
  initialTab: '',
  initialBreadcrumbs: [],
  shouldRenderFilter: true,
};

const ProductCategory = ({
  slug = defaultProps.slug,
  isNewLayout = defaultProps.isNewLayout,
  initialTab = defaultProps.initialTab,
  categoryData = defaultProps.categoryData,
  initialBrandsData = defaultProps.initialBrandsData,
  initialQueryParams = defaultProps.initialQueryParams,
  initialProductsData = defaultProps.initialProductsData,
  initialBreadcrumbs = defaultProps.initialBreadcrumbs,
  shouldRenderFilter = defaultProps.shouldRenderFilter,
  initialAlgoliaData = defaultProps.initialAlgoliaData,
}) => {
  const router = useRouter();
  const { slug: newSlug } = router.query;

  if (!isNewLayout) {
    return (
      <LegacyCategoryLayout
        slug={slug}
        initialTab={initialTab}
        categoryData={categoryData}
        initialBrandsData={initialBrandsData}
        initialQueryParams={initialQueryParams}
        initialProductsData={initialProductsData}
        initialBreadcrumbs={initialBreadcrumbs}
        shouldRenderFilter={shouldRenderFilter}
      />
    );
  }

  return (
    <CategoryLayout
      slug={newSlug}
      initialTab={initialTab}
      categoryData={categoryData}
      initialBreadcrumbs={initialBreadcrumbs}
      initialAlgoliaData={initialAlgoliaData}
    />
  );
};

ProductCategory.getLayout = FullWidthLayout;

ProductCategory.propTypes = {
  slug: PropTypes.string.isRequired,
  isNewLayout: PropTypes.bool.isRequired,
  categoryData: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    shortDescription: PropTypes.string,
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    parent: PropTypes.shape({}),
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialProductsData: PropTypes.shape({
    totalProducts: PropTypes.number,
    products: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialBrandsData: PropTypes.shape({
    totalBrands: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialAlgoliaData: PropTypes.shape({
    filters: PropTypes.arrayOf(PropTypes.shape({})),
    banners: PropTypes.arrayOf(PropTypes.shape({})),
    products: PropTypes.arrayOf(PropTypes.shape({})),
    totalProducts: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
    totalBrands: PropTypes.number,
    queryId: PropTypes.string,
  }),
  initialQueryParams: PropTypes.shape({}),
  initialTab: PropTypes.string,
  initialBreadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
  shouldRenderFilter: PropTypes.bool,
};

export const getServerSideProps = async ({ query, req }) => {
  const { fetcher, userCountry, queryParams } = await getSSRInitialData({ query, req });

  // Check if filter parameters exist in the URL
  const filterParamsExist = Object.keys(queryParams).some(
    (key) => key !== 'slug' // Assuming 'slug' is always part of the URL
  );

  const currentQueryParams = filterParamsExist ? queryParams : {};
  const isNewLayout = req?.cookies?.feature_flags
    ? JSON.parse(req.cookies.feature_flags)?.find((x) => x?.name === FEATURE_FLAGS.CATEGORY_ALGOLIA)?.value ?? true
    : true;

  const user = getUserFromCookie(req);

  const {
    categoryEndpoint,
    productsEndpoint,
    brandsEndpoint,
    categoryPageSearchWithAlgoliaProductsEndpoint,
    categoryPageSearchWithAlgoliaBrandsEndpoint,
  } = getSSRListsEndpoint({
    userCountry,
    queryParams: isNewLayout ? query : currentQueryParams,
  });

  const breadcrumbs = [];
  const allPromises = [];

  allPromises.push(fetcher.get(categoryEndpoint));

  try {
    if (!isNewLayout) {
      allPromises.push(fetcher.get(productsEndpoint));
      allPromises.push(fetcher.get(brandsEndpoint));
      const [categoryResponse, productsResponse, brandsResponse] = await Promise.all(allPromises);

      const categoryData = categoryResponse?.data;
      if (categoryResponse?.data) {
        if (categoryResponse?.data?.parent.categoryId) {
          breadcrumbs.push({ name: 'Creoate', slug: '/', type: 'homepage' });
        }
        breadcrumbs.push({ name: categoryResponse?.data.name, slug: categoryResponse?.data.slug, type: 'category' });
      }

      return {
        props: {
          slug: query.slug,
          isNewLayout,
          categoryData,
          initialProductsData: productsResponse?.data,
          initialBrandsData: brandsResponse?.data,
          initialQueryParams: currentQueryParams || {},
          initialTab: query?.tab || DEFAULT_TAB_LISTING_PAGES,
          initialBreadcrumbs: breadcrumbs,
        },
      };
    }

    const analyticsTags = generateAlgoliaAnalyticsTags('product-category', user).join(',');

    const categoryPageSearchWithAlgoliaProductsEndpointWithTags = `${categoryPageSearchWithAlgoliaProductsEndpoint}&analyticsTags=${encodeURIComponent(
      analyticsTags
    )}`;
    const categoryPageSearchWithAlgoliaBrandsEndpointWithTags = `${categoryPageSearchWithAlgoliaBrandsEndpoint}&analyticsTags=${encodeURIComponent(
      analyticsTags
    )}`;

    allPromises.push(fetcher.get(categoryPageSearchWithAlgoliaProductsEndpointWithTags));
    allPromises.push(fetcher.get(categoryPageSearchWithAlgoliaBrandsEndpointWithTags));

    const [categoryResponse, algoliaProductsResponse, algoliaBrandsResponse] = await Promise.all(allPromises);

    const categoryData = categoryResponse?.data;
    if (categoryResponse?.data) {
      if (categoryResponse?.data?.parent.categoryId) {
        breadcrumbs.push({ name: 'Creoate', slug: '/', type: 'homepage' });
      }
      breadcrumbs.push({ name: categoryResponse?.data.name, slug: categoryResponse?.data.slug, type: 'category' });
    }

    return {
      props: {
        slug: query.slug,
        categoryData,
        isNewLayout,
        initialTab: query?.tab || DEFAULT_TAB_LISTING_PAGES,
        initialBreadcrumbs: breadcrumbs,
        initialAlgoliaData: {
          filters: algoliaProductsResponse?.data?.filters || [],
          banners: algoliaProductsResponse?.data?.banners || [],
          products: algoliaProductsResponse?.data?.products || [],
          totalProducts: algoliaProductsResponse?.data?.total || 0,
          brands: algoliaBrandsResponse?.data?.brands || [],
          totalBrands: algoliaBrandsResponse?.data?.total || 0,
          queryId: algoliaProductsResponse?.data?.queryID || '',
        },
      },
    };
  } catch (err) {
    logger.error({ instance: 'product-category', type: 'getServerSideProps' }, err);

    if (err.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    return {
      redirect: {
        permanent: false,
        destination: routes.page.root,
      },
    };
  }
};

export default ProductCategory;
